<template>
  <div v-if="tvspot && tvspot.template">
    <!-- Dialog to edit TVSpot.template_data -->
    <v-dialog
      lazy
      scrollable
      persistent
      :value="show"
      :width="800"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      @input="(val) => $emit('update:show', val)"
    >
      <v-card>
        <v-card-title class="pb-0">
          <v-layout column>
            <v-layout row>
              <v-icon
                class="mr-3"
                color="msr"
                large
              >
                tv
              </v-icon>
              <v-layout column>
                <v-layout align-center>
                  <div class="headline">
                    {{ $t('tvtemplatesEditTemplateDataDialogTitle') }}
                  </div>
                  <v-btn
                    class="ml-auto mr-0 my-0"
                    :disabled="loading"
                    flat
                    icon
                    :title="$t('close')"
                    @click="abort()"
                  >
                    <v-icon>close</v-icon>
                  </v-btn>
                </v-layout>
                <div class="subheading grey--text text--darken-1 nowrap">
                  {{ tvspot.title }}
                </div>
              </v-layout>
            </v-layout>
          </v-layout>
        </v-card-title>
        <v-divider class="mt-2" />
        <v-card-text>
          <div class="subheading mt-2">
            <span>{{ $t('tvtemplatesSpotFromTemplate') }}:&nbsp;</span>
            <span
              class="msr--text pseudo-anchor"
              @click="goToTemplatePageWhenConfirmed(tvspot.template)"
            >{{ tvspot.template.title }}</span>
            <div class="text--grey caption mt-2">
              {{ $t('tvtemplatesEditTemplateDataDialogHint') }}
            </div>
          </div>
          <!-- Branding Dropdown -->
          <v-layout class="mb-2">
            <tv-template-branding-widget
              class="ml-auto"
              :title="lastBrandingApplied ? lastBrandingApplied.name : null"
              :overlay-options="tvspot.template.overlay_options"
              :brandings="brandings"
              :active="lastBrandingApplied !== null"
              @applied="onBrandingApplied"
            />
          </v-layout>
          <!-- TVTemplate Preview -->
          <v-layout
            class="mb-2"
            justify-center
          >
            <tv-template-preview
              ref="preview"
              :html-overlay="tvspot.template.html_overlay"
              :background-image="tvspot.template.video_poster_for_editing"
              :tvtemplate="tvspot.template"
              :width="tvspot.tvformat.width"
              :height="tvspot.tvformat.height"
              :max-width="$store.state.common.largeDialogWidth"
              :max-height="320"
              :is-multiscreen="tvspot.is_multiscreen"
              show-zoom-buttons
              :onload-callback="populateIframeWithInitialSpotTemplateData"
            />
          </v-layout>

          <!-- UI for template_data from overlay_options -->
          <v-layout>
            <overlay-data
              ref="overlaydata"
              :key="overlayDataWidgetRenderkey"
              :overlay-options="tvspot.template.overlay_options"
              class="grow-1"
              @changed="refreshIframeWithOverlayData()"
              @change-image="onImageChangeRequested"
              @valid="onTemplateDataValidityChanged"
            />
          </v-layout>
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-3">
          <v-layout
            align-center
            wrap
          >
            <v-spacer />
            <content-creator-plan-status-hint
              class="mr-3 grey--text text--darken-2 subheading"
              :class="{'mb-2 nowrap': $vuetify.breakpoint.xs}"
            />
            <btn-ok
              :loading="loading"
              round
              :dark="enableProduceButton"
              :disabled="!enableProduceButton"
              :label="$t('tvtemplatesEditTemplateDataDialogConfirmBtn')"
              @click="confirm()"
            />
          </v-layout>
          <v-spacer />
        </v-card-actions>
        <v-spacer />
      </v-card>
    </v-dialog>
    <!-- TVTemplate Image Replacement -->
    <image-replacement
      ref="imagereplacement"
      @cropped="onImageChangeConfirmed"
    />
  </div>
</template>

<script>
import {getDefaultOverlayDataForOverlayOptions} from '@/mixins/overlayOptions.js';
import {onTVTemplateImageReplacementCropped} from '@/mixins/imageReplacement';

import BtnOk from '@/components/ButtonOk';
import ContentCreatorPlanStatusHint from '@/components/ContentCreatorPlanStatusHint.vue';
import ImageReplacement from '@/components/ImageReplacement';
import OverlayData from '@/components/OverlayData';
import TvTemplateBrandingWidget from '@/components/TvTemplateBrandingWidget.vue';
import TvTemplatePreview from '@/components/TvTemplatePreview';

const defaults = {
  loading: false,
  isTemplateDataValid: false,
};

export default {
  components: {
    BtnOk,
    ContentCreatorPlanStatusHint,
    ImageReplacement,
    OverlayData,
    TvTemplateBrandingWidget,
    TvTemplatePreview,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    tvspot: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: defaults.loading,
      isTemplateDataValid: defaults.isTemplateDataValid,
      overlayDataWidgetRenderkey: 0,
    };
  },
  computed: {
    brandings() {
      return this.$store.state.common.brandings;
    },
    lastBrandingApplied: {
      get() {
        return this.$store.state.common.lastBrandingApplied;
      },
      set(branding) {
        this.$store.commit('SET_LAST_BRANDING_APPLIED', branding);
      },
    },
    contentCreatorBookingIsValidForProduction() {
      return (
        this.$store.getters.userHasContentCreatorBooking &&
        this.$userprofile &&
        this.$userprofile.content_creator_plan_booking &&
        this.$userprofile.content_creator_plan_booking.is_valid_for_production
      );
    },
    enableProduceButton() {
      return (
        this.$store.getters.userHasContentCreatorBooking &&
        this.contentCreatorBookingIsValidForProduction &&
        this.isTemplateDataValid
      );
    },
  },
  watch: {
    /**
     * Reset when closed, populate when opened.
     */
    show: function (show) {
      if (!show) {
        this.reset();
        return;
      }

      // Populate UI.
      this.loading = true;
      this.$store
        .dispatch('MAYBE_LIST_BRANDINGS')
        .then(() => this.initializeBranding())
        .finally(() => (this.loading = false));

      this.overlayDataWidgetRenderkey += 1;
      this.$nextTick(() => {
        this.populateOverlayDataWidgetsWithInitialSpotTemplateData();
      });
    },
  },
  methods: {
    onImageChangeConfirmed(payload) {
      onTVTemplateImageReplacementCropped(
        this.tvspot.template.id,
        this.tvspot.overlay_options,
        payload.fieldName,
        payload.file,
        payload.originalFilename
      );
    },
    reset() {
      this.loading = defaults.loading;
      this.isTemplateDataValid = defaults.isTemplateDataValid;
    },
    closeDialog() {
      this.$emit('update:show', false);
    },
    abort() {
      this.closeDialog();
    },
    injectTvSpotDurationIntoTemplateData(overlayData) {
      overlayData.duration = this.tvspot.duration;
    },
    initializeBranding() {
      if (
        this.tvspot &&
        this.tvspot.template_data &&
        this.tvspot.template_data.branding &&
        this.tvspot.template_data.branding.id
      ) {
        const matchingBranding = this.brandings.filter(
          (branding) => branding.id == this.tvspot.template_data.branding.id
        )[0];
        if (matchingBranding) {
          this.lastBrandingApplied = matchingBranding;
        }
      }
    },
    onBrandingApplied(context) {
      this.lastBrandingApplied = context.branding;
      this.tvspot.template_data.branding = context.branding;
      this.refreshIframeWithOverlayData();
    },
    /**
     * The tvspot.template_data.branding may be outdated, update if needed.
     */
    getTemplateDataWithUpdatedBranding() {
      let newerBrandingData = {};
      if (this.tvspot.template_data && this.tvspot.template_data.branding) {
        const matchingBranding = this.brandings.filter(
          (branding) => branding.id == this.tvspot.template_data.branding.id
        )[0];
        if (matchingBranding) {
          newerBrandingData = {branding: matchingBranding};
        }
      }
      return {
        ...this.tvspot.template_data,
        ...newerBrandingData,
      };
    },
    getCurrentTemplateData() {
      const overlayDataWidget = this.$refs.overlaydata;
      if (!overlayDataWidget) {
        return {};
      }

      const templateData = this.getTemplateDataWithUpdatedBranding();
      this.injectTvSpotDurationIntoTemplateData(templateData);

      // Merge current over prior, so things like the Branding are
      // preserved, but user changes are applied on top of previous
      // values.
      const currentOverlayData = overlayDataWidget.getOverlayData();
      const mergedOverlayData = {
        ...templateData,
        ...currentOverlayData,
      };
      return mergedOverlayData;
    },
    confirm() {
      const currentTemplateData = this.getCurrentTemplateData();
      this.updateTvspotTemplateData(this.tvspot, currentTemplateData);
    },
    updateTvspotTemplateData(tvspot, templateData) {
      this.loading = true;
      this.$emit('edit-begin');
      this.$api
        .updateTvspotWithTemplateData(tvspot.id, templateData)
        .then(() => {
          const message = this.$t('tvtemplatesEditTvspotSuccess').replace(
            '{{ title }}',
            tvspot.title
          );
          this.$bus.$emit('alert-success', {
            message: message,
            translate: false,
            timeout: 8000,
          });

          // Update ContentCreatorBooking status info (e.g. quota left).
          this.$store.dispatch('RETRIEVE_CURRENT_USER');

          this.$store.commit('SET_FORCE_REFETCH_CONTENTS_ON_NEXT_VISIT', true);
        })
        .finally(() => {
          this.closeDialog(); // This also resets the loading spinner.
          this.$emit('edit-finish');
        });
    },
    /**
     * Delegate request to image replacement handler.
     */
    onImageChangeRequested(opts) {
      this.$refs.imagereplacement.onImageChangeRequested(opts);
    },
    onTemplateDataValidityChanged(allFieldsAreValid) {
      this.isTemplateDataValid = allFieldsAreValid;
    },
    populateOverlayDataWidgetsWithInitialSpotTemplateData() {
      // Populate the overlay-data widgets with the initial template_data.
      // The iframe preview is populated via the onload-callback above.
      const templateDataFieldNames = Object.keys(this.tvspot.template_data);

      for (let fieldName of templateDataFieldNames) {
        const fieldValue = this.tvspot.template_data[fieldName];
        this.$bus.$emit('set-overlay-data-field-value', fieldName, fieldValue);
      }
    },
    populateIframeWithInitialSpotTemplateData() {
      const templateData = this.getTemplateDataWithUpdatedBranding();

      // To make sure the template_data (which may be older) includes
      // all necessary fields, we merge it over the default overlay_data.
      const defaultOverlayData = getDefaultOverlayDataForOverlayOptions(
        this.tvspot.template.overlay_options
      );
      const mergedOverlayData = {
        ...defaultOverlayData,
        ...templateData,
      };

      const previewWidget = this.$refs.preview;
      previewWidget.updateOverlayData(mergedOverlayData);
    },
    refreshIframeWithOverlayData() {
      const overlayDataWidget = this.$refs.overlaydata;
      if (!overlayDataWidget) {
        return;
      }
      const currentTemplateData = this.getCurrentTemplateData();
      const previewWidget = this.$refs.preview;
      previewWidget.updateOverlayData(currentTemplateData);
    },
    goToTemplatePageWhenConfirmed(tvtemplate) {
      const confirmed = confirm(this.$t('tvtemplatesGoToTemplatePageConfirm'));
      if (!confirmed) {
        return;
      }
      this.$router.push({
        name: 'tvtemplate',
        params: {tvtemplateId: tvtemplate.id},
      });
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/EditTemplateDataDialog.vue